import React, { useState } from "react";
import { Modal, Button, Form, Spinner, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faUserShield,
  faPause,
  faPlay,
  faBan,
  faCamera,
  faLock,
  faClock,
  faEyeSlash,
  faEye,
  faCheck,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { InvisilatorService } from "Services/InvisilatorService";

interface Props {
  show: boolean;
  examId: string;
  onClose: () => void;
  onSuccess?: any;
}

const PERMISSIONS_META = [
  { key: "canPauseExam", label: "Pause Exam", icon: faPause },
  { key: "canResumeExam", label: "Resume Exam", icon: faPlay },
  { key: "canTerminateExam", label: "Terminate Exam", icon: faBan },
  { key: "canForceCameraOn", label: "Force Camera On", icon: faCamera },
  { key: "canLockScreen", label: "Lock Screen", icon: faLock },
  { key: "canExtendTime", label: "Extend Time", icon: faClock },
];

export default function AddInvigilatorModal({
  show,
  examId,
  onClose,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState<any>({
    password: "",
    name: "",
    email: "",
    permissions: {
      canPauseExam: false,
      canResumeExam: false,
      canTerminateExam: false,
      canForceCameraOn: false,
      canLockScreen: false,
      canExtendTime: false,
    },
  });

  const copyPassword = async () => {
    if (!formData.password) return;
    await navigator.clipboard.writeText(formData.password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const togglePermission = (key: string) => {
    setFormData((prev: any) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key],
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const payload = {
        exam: examId,
        password: formData.password,
        name: formData.name,
        email: formData.email,
        permissions: formData.permissions,
      };

      const res = await InvisilatorService.createInvigilator(payload);

      if (res?.status === 200 || res?.status === 201) {
        onSuccess?.();
        onClose();
      }
    } catch (err: any) {
      alert(err?.response?.data?.message || "Failed to create invigilator");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">Add Invigilator</Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-light">
        <div className="bg-white rounded p-4 mb-4 shadow-sm">
          <h6 className="fw-bold mb-3">
            <FontAwesomeIcon
              icon={faUserShield}
              className="me-2 text-primary"
            />
            Basic Information
          </h6>

          <div className="row">
            <div className="col-md-6 mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Name
              </Form.Label>
              <Form.Control
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Email
              </Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label className="fw-semibold">Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPass ? "text" : "password"}
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />

                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPass(!showPass)}
                >
                  <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
                </Button>

                <Button variant="outline-primary" onClick={copyPassword}>
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                </Button>
              </InputGroup>
            </div>

            <div className="col-md-6 mb-3">
              <Form.Label>
                <FontAwesomeIcon icon={faUserShield} className="me-2" />
                Exam
              </Form.Label>
              <Form.Control value={examId} disabled />
            </div>
          </div>
        </div>

        {/* <div className="bg-white rounded p-4 shadow-sm">
          <h6 className="fw-bold mb-3">Permissions</h6>

          <div className="row">
            {PERMISSIONS_META.map((p) => (
              <div className="col-md-4 mb-3" key={p.key}>
                <div className="border rounded p-3 d-flex justify-content-between">
                  <span>
                    <FontAwesomeIcon icon={p.icon} className="me-2" />
                    {p.label}
                  </span>
                  <Form.Check
                    type="switch"
                    checked={formData.permissions[p.key]}
                    onChange={() => togglePermission(p.key)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div> */}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            "Add Invigilator"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
